import { Pencil, Trash2 } from "lucide-react";
import StatusPill from "shared/StatusPill";
import LoggedOn from "shared/LoggedOn";
import RowActionsMenu from "shared/RowActionsMenu";
import type { RowAction } from "shared/RowActionsMenu";
import type { EvaluationItem } from "../modal/evaluate_context";
import { CRITERIA, evaluationAverage, formatDate, formatScore, scaleRemark, scoreTone } from "./evaluationConfig";
import ScoreMeter from "./ScoreMeter";

interface EvaluationCardProps {
  evaluation: EvaluationItem;
  canEditDelete: boolean;
  onEdit: (e: EvaluationItem) => void;
  onDelete: (id: number) => void;
}

/** One evaluation: date, overall average, every criterion, then the comments. */
export default function EvaluationCard({ evaluation, canEditDelete, onEdit, onDelete }: EvaluationCardProps) {
  const avg = evaluationAverage(evaluation);
  const actions: RowAction[] = [
    { label: "Edit", icon: Pencil, onClick: () => onEdit(evaluation) },
    { label: "Delete", icon: Trash2, destructive: true, onClick: () => onDelete(evaluation.evaluation_id) },
  ];

  return (
    <article className="bg-surface border border-border rounded-xl">
      <header className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-border">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="text-sm font-semibold text-foreground">{formatDate(evaluation.created_at)}</h3>
          {avg != null && (
            <StatusPill tone={scoreTone(avg)} title="Average of the scored criteria (out of 5)">
              {avg.toFixed(1)} / 5
            </StatusPill>
          )}
          {evaluation.created_by && <span className="text-xs text-muted-foreground">by {evaluation.created_by}</span>}
        </div>
        {canEditDelete && <RowActionsMenu actions={actions} />}
      </header>

      <div className="px-5 pt-3">
        <LoggedOn recordDate={evaluation.created_at} recordedAt={evaluation.recorded_at} />
      </div>

      <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3">
        {CRITERIA.map(({ key, label }) => {
          const v = evaluation[key];
          return (
            <div key={key}>
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <span className="text-xs text-foreground">{label}</span>
                <span className="text-xs text-muted-foreground">{v > 0 ? `${formatScore(v)} · ${scaleRemark(v)}` : "Not scored"}</span>
              </div>
              <ScoreMeter value={v} />
            </div>
          );
        })}
      </div>

      {(evaluation.general_comments || evaluation.extra_comments) && (
        <div className="px-5 pb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {evaluation.general_comments && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">General comments</p>
              <p className="text-sm text-foreground whitespace-pre-wrap bg-surface-muted rounded-lg p-3">{evaluation.general_comments}</p>
            </div>
          )}
          {evaluation.extra_comments && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Extra comments</p>
              <p className="text-sm text-foreground whitespace-pre-wrap bg-surface-muted rounded-lg p-3">{evaluation.extra_comments}</p>
            </div>
          )}
        </div>
      )}

      {evaluation.updated_by && (
        <footer className="px-5 py-2.5 border-t border-border text-xs text-muted-foreground">
          Last updated by {evaluation.updated_by}
          {evaluation.updated_at ? ` · ${formatDate(evaluation.updated_at)}` : ""}
        </footer>
      )}
    </article>
  );
}
